import { useEffect } from 'react';
import confetti from 'canvas-confetti';

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  isRefreshingCredits?: boolean;
}

export default function PaymentSuccessModal({
  isOpen,
  onClose,
  isRefreshingCredits = false,
}: PaymentSuccessModalProps) {
  // Trigger confetti when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaults = {
        startVelocity: 26,
        spread: 72,
        ticks: 220,
        zIndex: 60,
        colors: ['#22c55e', '#34d399', '#fde047', '#f59e0b', '#ffffff'],
      };

      const fire = (particleRatio: number, options: Parameters<typeof confetti>[0]) => {
        confetti({
          ...defaults,
          ...options,
          particleCount: Math.floor(180 * particleRatio),
        });
      };

      fire(0.45, { origin: { x: 0.5, y: 0.62 } });

      const timeouts = [
        window.setTimeout(() => fire(0.25, { angle: 60, origin: { x: 0, y: 0.72 } }), 140),
        window.setTimeout(() => fire(0.25, { angle: 120, origin: { x: 1, y: 0.72 } }), 260),
        window.setTimeout(() => fire(0.18, { spread: 100, startVelocity: 20, origin: { x: 0.22, y: 0.5 } }), 420),
        window.setTimeout(() => fire(0.18, { spread: 100, startVelocity: 20, origin: { x: 0.78, y: 0.5 } }), 520),
      ];

      return () => {
        timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-emerald-400/25 bg-[linear-gradient(180deg,#151515_0%,#0b0b0b_100%)] shadow-[0_30px_90px_rgba(0,0,0,0.45)] animate-fadeIn">
        {/* Success Glow Effect */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(74,222,128,0.18),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(250,204,21,0.12),transparent_28%)]"></div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 rounded-full p-2 text-white/60 transition-all hover:bg-white/10 hover:text-white"
          disabled={isRefreshingCredits}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="relative p-8">
          {/* Success Icon */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              {/* Animated ring */}
              <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping"></div>
              {/* Icon container */}
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/30">
                <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="mb-2 text-center text-3xl font-black tracking-tight text-white">
            Payment Successful!
          </h2>
          <p className="mb-6 text-center text-base text-gray-300">
            Your credits are already in your account and ready to use.
          </p>

          {/* Info Box */}
          <div className="mb-6 rounded-2xl border border-emerald-400/16 bg-emerald-500/8 p-4">
            <div className="flex items-start gap-3">
              <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-gray-300">
                <p className="mb-1 font-semibold text-emerald-300">You are ready to go</p>
                <p className="leading-6 text-gray-400">
                  Continue back to your workspace and start creating campaigns right away.
                </p>
              </div>
            </div>
          </div>

          {isRefreshingCredits && (
            <div className="mb-6 rounded-xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
              Closing your checkout and returning to the workspace...
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={onClose}
            disabled={isRefreshingCredits}
            className="w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-3.5 font-semibold text-white transition-all hover:shadow-lg hover:shadow-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isRefreshingCredits ? 'Returning to workspace...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
